import { useState } from 'react';
import type { UndecoratedUnisonProject } from '@witmetrics/utils';
import {
  asyncMap,
  findByUuid,
  removeFromArray,
} from '@witmetrics/utils/arrays';
import { excludeKeys } from '@witmetrics/utils/objects';
import { isAiProjectType } from '@witmetrics/utils/unisonProjects';
import { UnauthorizedError } from '@witmetrics/utils/errors';
import {
  useActivePractice,
  useDateFormatPreference,
  useDispatch,
} from '@witmetrics/data-store';
import { useAppState } from '@/providers/AppStateProvider';
import { useToggle } from '@/hooks/useToggle';
import { useLoadedFiles } from '@/providers/LoadedFilesProvider';
import { createProjectFormSubmission } from '@/api/utils/projects';
import { createProjectTask } from '@/api/utils/tasks';
import {
  type PendingSubmission,
  type PendingTask,
  useNewCase,
} from '@/providers/NewCaseProvider';
import NoFilesWarningDialog from '@/dialogs/NoFilesWarningDialog';
import { AiIcon } from '@/icons';
import Dialog, { DialogBody, DialogTitle } from '@/components/Dialog';
import CreationProgressDialog from './CreationProgressDialog';
import SuccessDialog from './SuccessDialog';
import CaseStepsBlock from './steps/CaseStepsBlock';
import CaseFooter from './CaseFooter';
import type {
  CreationStep,
  CreationStepStatus,
  MergedUploadedFiles,
  WizardStep,
} from './types';
import {
  CREATION_STEP,
  CREATION_STEP_STATUS,
  DIALOG_TITLE,
  RELU_LOWER_ARCH,
  RELU_UPPER_ARCH,
  WIZARD_STEPS,
} from './constants';
import {
  buildNotes,
  classes,
  createNewProject,
  createProjectFileCollection,
  createReluService,
  fetchTokenCount,
  hasSelectedFiles,
  isSaveEnabled,
} from './utils';

const { CREATE_PROJECT, UPLOAD_FILES, SAVE_PENDING_ITEMS, INITIALIZE_SERVICE } =
  CREATION_STEP;

const { IN_PROGRESS, DONE, FAILED } = CREATION_STEP_STATUS;

type NewCaseDialogProps = {
  onClose: () => void;
};

export default function NewCaseDialog({ onClose }: NewCaseDialogProps) {
  const dispatch = useDispatch();
  const { onSelectProject, onApiError, onToggleUnloadWarning } = useAppState();
  const activePractice = useActivePractice();
  const dateFormat = useDateFormatPreference();
  const {
    projectTypeId,
    serviceType,
    isPreVeneers,
    leadId,
    selectedFiles,
    pendingSubmissions,
    pendingTasks,
    onChangeFile,
    name,
    externalRef,
    notes,
    params,
    selectedTeeth,
  } = useNewCase();
  const { addLoadedFiles } = useLoadedFiles();
  const [activeStep, setActiveStep] = useState<WizardStep>(
    WIZARD_STEPS.SELECT_PATIENT
  );
  const [creationStep, setCreationStep] = useState<CreationStep>();
  const [createdProjectId, setCreatedProjectId] = useState<number>();
  const [submissionStatuses, setSubmissionStatuses] = useState<
    Record<string, CreationStepStatus>
  >({});
  const [taskStatuses, setTaskStatuses] = useState<
    Record<string, CreationStepStatus>
  >({});
  const [isFileWarningShown, toggleFileWarning] = useToggle();
  const [showSuccess, toggleShowSuccess] = useToggle();
  const [isSaving, toggleSaving] = useToggle();

  const handleClose = () => {
    if (!isSaving) onClose();
  };

  const handleSave = async (ignoreFileCount?: boolean) => {
    try {
      if (isSaving) return; // Avoid multiple submissions
      if (!ignoreFileCount && !hasSelectedFiles(selectedFiles)) {
        // TODO: If it's a scan service, confirm what files it needs
        return toggleFileWarning(true);
      }
      toggleSaving(true);
      setCreationStep(CREATE_PROJECT);
      onToggleUnloadWarning(true);
      const data = await createNewProject(buildProjectData());
      setCreatedProjectId(data.unisonProject.id);
      handleProjectDispatches(data);
      setCreationStep(UPLOAD_FILES);
      if (!isAiProjectType(projectTypeId)) {
        // TODO: Veneers cases

        // Just need to upload the files for an Aligners case
        await handleSaveAlignerFiles(data.unisonProject);
      } else if (serviceType) {
        // Need to create everything in our API & send to Relu
        console.log('Creating an AI-powered case...');
        const fileData = await handleSaveServiceFiles(data.unisonProject);
        setCreationStep(INITIALIZE_SERVICE);
        const serviceData = await createReluService({
          projectId: data.unisonProject.id,
          serviceType,
          params,
          selectedFiles,
          parentCollection: fileData.serviceCollection!,
          inputFilesCollection: fileData.inputFilesCollection!,
          onUploadProgress: onChangeFile,
        });
        dispatch.addScanServices([serviceData.service]);
        dispatch.addProjectScanServices([serviceData.projectService]);
        dispatch.addScanServiceFileCollections([
          {
            ...serviceData.serviceCollection,
            fileCollection: fileData.serviceCollection!,
          },
        ]);
        handleFileDispatches(serviceData.uploads);
      } else {
        throw Error(`Unsupported project type: ${projectTypeId}`);
      }
      setCreationStep(SAVE_PENDING_ITEMS);
      await Promise.all([
        handleSavePendingSubmissions(data.unisonProject.id),
        handleSavePendingTasks(data.unisonProject.id),
      ]);
      onToggleUnloadWarning(false);
      onSelectProject(data.unisonProject.id);
      toggleSaving(false);
      setCreationStep(undefined);
      if (serviceType) toggleShowSuccess(true);
      else handleClose();
    } catch (err) {
      toggleSaving(false);
      onApiError(err, 'Error creating case');
    }
  };

  const handleSavePendingSubmission = async (
    submission: PendingSubmission,
    projectId: number
  ) => {
    try {
      setSubmissionStatuses((prev) => ({
        ...prev,
        [submission.uuid]: IN_PROGRESS,
      }));
      const result = await createProjectFormSubmission(
        projectId,
        submission.versionId,
        submission.answers
      );
      dispatch.addSubmissions([result.submission]);
      dispatch.addProjectSubmissions([result.projectSubmission]);
      setSubmissionStatuses((prev) => ({ ...prev, [submission.uuid]: DONE }));
    } catch (err) {
      setSubmissionStatuses((prev) => ({ ...prev, [submission.uuid]: FAILED }));
      if (err instanceof UnauthorizedError) {
        onApiError(err, 'Error saving form submission', () =>
          handleSavePendingSubmission(submission, projectId)
        );
      }
    }
  };

  const handleSavePendingSubmissions = async (projectId: number) => {
    await asyncMap(pendingSubmissions, (s) =>
      handleSavePendingSubmission(s, projectId)
    );
  };

  const handleRetrySubmission = (uuid: string) => {
    if (!createdProjectId) return;
    const submission = findByUuid(pendingSubmissions, uuid);
    if (!submission) return;
    return handleSavePendingSubmission(submission, createdProjectId);
  };

  const handleSavePendingTask = async (
    task: PendingTask,
    projectId: number
  ) => {
    try {
      setTaskStatuses((prev) => ({ ...prev, [task.uuid]: IN_PROGRESS }));
      const { task: createdTask, unisonProjectTask } = await createProjectTask(
        projectId,
        {
          dueAt: task.dueAt,
          assignedTo: task.assignedTo,
          description: task.description,
        }
      );
      dispatch.addTasks([createdTask]);
      dispatch.addProjectTasks([unisonProjectTask]);
      setTaskStatuses((prev) => ({ ...prev, [task.uuid]: DONE }));
    } catch (err) {
      setTaskStatuses((prev) => ({ ...prev, [task.uuid]: FAILED }));
      if (err instanceof UnauthorizedError) {
        onApiError(err, 'Error saving task', () =>
          handleSavePendingTask(task, projectId)
        );
      }
    }
  };

  const handleSavePendingTasks = async (projectId: number) => {
    await asyncMap(pendingTasks, (t) => handleSavePendingTask(t, projectId));
  };

  const handleRetryTask = (uuid: string) => {
    if (!createdProjectId) return;
    const task = findByUuid(pendingTasks, uuid);
    if (!task) return;
    return handleSavePendingTask(task, createdProjectId);
  };

  const handleSaveAlignerFiles = async (
    unisonProject: UndecoratedUnisonProject
  ) => {
    const collectionData = await createProjectFileCollection(
      unisonProject,
      excludeKeys(selectedFiles, [RELU_UPPER_ARCH, RELU_LOWER_ARCH]),
      onChangeFile,
      dateFormat
    );
    handleCollectionDispatches(collectionData);
    return collectionData;
  };

  const handleSaveServiceFiles = async (
    unisonProject: UndecoratedUnisonProject
  ) => {
    //Keep 1st upper/lower scans for Relu, rest get uploaded normally
    const collectionData = await createProjectFileCollection(
      unisonProject,
      {
        ...selectedFiles,
        UPPER_ARCH: removeFromArray(selectedFiles.UPPER_ARCH, 0),
        LOWER_ARCH: removeFromArray(selectedFiles.LOWER_ARCH, 0),
      },
      onChangeFile,
      dateFormat,
      serviceType
    );
    handleCollectionDispatches(collectionData);
    return collectionData;
  };

  const buildProjectData = () => {
    return {
      name: name.trim(),
      externalRef: externalRef.trim(),
      description: notes.trim(),
      notes: buildNotes(
        notes,
        params,
        selectedTeeth,
        projectTypeId,
        isPreVeneers
      ),
      userId: leadId,
      practiceId: activePractice!.id,
      projectTypeId,
    };
  };

  const handleProjectDispatches = (
    data: Awaited<ReturnType<typeof createNewProject>>
  ) => {
    dispatch.addProjects([data.unisonProject]);
    if (data.unisonProjectNote) {
      dispatch.addProjectNotes([data.unisonProjectNote]);
    }
  };

  const handleCollectionDispatches = (
    data: Awaited<ReturnType<typeof createProjectFileCollection>>
  ) => {
    dispatch.addProjectFileCollections(data.unisonProjectFileCollections);
    dispatch.addFileCollections(data.fileCollections);
    handleFileDispatches(data);
  };

  const handleFileDispatches = (data: MergedUploadedFiles) => {
    dispatch.addFiles(data.files);
    dispatch.addFileVersions(data.fileVersions);
    dispatch.addFileCollectionFiles(data.fileCollectionFiles);
    addLoadedFiles(data.loadedFiles);
  };

  if (showSuccess) return <SuccessDialog onClose={handleClose} />;

  return (
    <>
      {isFileWarningShown && (
        <NoFilesWarningDialog
          onContinue={() => {
            toggleFileWarning(false);
            handleSave(true);
          }}
          onClose={() => toggleFileWarning(false)}
        />
      )}
      {isSaving && creationStep && (
        <CreationProgressDialog
          step={creationStep}
          submissionStatuses={submissionStatuses}
          taskStatuses={taskStatuses}
          onRetrySubmission={handleRetrySubmission}
          onRetryTask={handleRetryTask}
        />
      )}
      <Dialog
        disableEscapeKeyDown
        disableBackdropClose
        className={classes.dialog}
        onClose={handleClose}>
        <DialogTitle
          className={classes.dialogTitle}
          icon={
            isAiProjectType(projectTypeId) && (
              <AiIcon className={classes.aiIcon} color="yellow-4" />
            )
          }
          title={`New ${DIALOG_TITLE[projectTypeId] || ''} Case`}
          subtitle={`Create a new ${isAiProjectType(projectTypeId) ? 'AI-powered' : ''} case in 4 easy steps`}
          onClose={handleClose}
        />
        <DialogBody className={classes.body}>
          <CaseStepsBlock activeStep={activeStep} onNavigate={setActiveStep} />
        </DialogBody>
        <CaseFooter
          isSaving={isSaving}
          isSkippable={!serviceType && activeStep === WIZARD_STEPS.SELECT_FILES}
          isSaveEnabled={isSaveEnabled(
            activeStep,
            projectTypeId,
            serviceType,
            selectedFiles,
            leadId,
            params,
            notes
          )}
          step={activeStep}
          onFetchTokenCount={fetchTokenCount(serviceType!)}
          onNext={setActiveStep}
          onSave={handleSave}
          onClose={onClose}
        />
      </Dialog>
    </>
  );
}
